import {
	CloudUpload,
	Dashboard as DashboardIcon,
	PeopleAlt,
} from "@mui/icons-material";
import {
	Box,
	Divider,
	List,
	ListItemButton,
	ListItemIcon,
	ListItemText,
	Typography,
	alpha,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { NavLink } from "react-router-dom";
import * as hooks from "lib/hooks";
import { DrawerNavigationRoute } from "enums/drawerNavigation";

export const StyledDrawer = styled(Box)(({ theme }) => ({
	width: 280,
	backgroundColor: theme.palette.background.paper,
	height: "100%",
	overflowY: "auto",
	boxShadow: theme.shadows[2],
	display: "flex",
	flexDirection: "column",
}));

export const DrawerHeader = styled(Box)(({ theme }) => ({
	"padding": theme.spacing(2.5, 3),
	"display": "flex",
	"alignItems": "center",
	"justifyContent": "space-between",
	"background": `linear-gradient(
    135deg,
    ${alpha(theme.palette.primary.main, 0.7)} 0%,
    ${alpha(theme.palette.primary.main, 0.9)} 100%
  )`,
	"color": theme.palette.primary.contrastText,
	"boxShadow": `inset 0 -1px 0 ${alpha(theme.palette.common.black, 0.1)}`,
	"borderRadius": 2,
	"minHeight": theme.spacing(7),

	// Optional overflow and scroll handling if needed
	"overflowX": "auto",

	// Scrollbar styling (only shows if content overflows)
	"&::-webkit-scrollbar": {
		height: "6px",
	},
	"&::-webkit-scrollbar-track": {
		background: "transparent",
	},
	"&::-webkit-scrollbar-thumb": {
		backgroundColor: alpha(theme.palette.primary.main, 0.25),
		borderRadius: "3px",
	},
	"&::-webkit-scrollbar-thumb:hover": {
		backgroundColor: alpha(theme.palette.primary.main, 0.4),
	},
}));

export const StyledNavLink = styled(NavLink)(({ theme }) => ({
	"textDecoration": "none",
	"color": theme.palette.text.primary,
	"display": "block",
	"position": "relative",

	"& .MuiListItemButton-root": {
		"margin": theme.spacing(0.5, 1.5),
		"borderRadius": theme.shape.borderRadius,
		"transition": theme.transitions.create(
			["background-color", "color", "padding"],
			{
				duration: theme.transitions.duration.shorter,
			}
		),
		"padding": theme.spacing(1, 2),

		"&:hover": {
			backgroundColor: alpha(theme.palette.primary.main, 0.08),
			paddingLeft: theme.spacing(2.5),
		},
	},

	"& .MuiListItemIcon-root": {
		minWidth: 36,
		color: theme.palette.text.secondary,
		transition: theme.transitions.create("color", {
			duration: theme.transitions.duration.shorter,
		}),
	},

	"& .MuiListItemText-primary": {
		fontSize: "0.935rem",
		fontWeight: 500,
		color: theme.palette.text.primary,
	},

	"&.active": {
		"& .MuiListItemButton-root": {
			backgroundColor: alpha(theme.palette.primary.main, 0.12),
			paddingLeft: theme.spacing(2.5),
		},
		"& .MuiListItemIcon-root": {
			color: theme.palette.primary.main,
		},
		"& .MuiListItemText-primary": {
			fontWeight: 600,
			color: theme.palette.primary.main,
		},
	},
}));

export const StyledList = styled(List)(({ theme }) => ({
	padding: theme.spacing(1.5, 0),
}));

export const StyledDivider = styled(Divider)(({ theme }) => ({
	margin: theme.spacing(0.5, 0),
}));

export const DrawerNavigation = ({
	onClick,
}: {
	onClick: (p: string, i: int) => void;
}) => {
	const routeManager = hooks.UseRouteManager();
	const permissions = hooks.UseHasPermission();
	const getIconForRoute = (routeName: string) => {
		switch (routeName) {
			case "Dashboard":
				return <DashboardIcon />;
			case "UserManagement":
				return <PeopleAlt />;
			case "Upload":
				return <CloudUpload />;
			default:
				return <DashboardIcon />; // Default icon, in case no match
		}
	};

	return (
		<StyledDrawer>
			<DrawerHeader>
				<Typography
					variant="h5"
					fontWeight={700} // extra-bold
					sx={{ textAlign: "center" }}
				>
					Navigation
				</Typography>
			</DrawerHeader>

			<StyledList>
			{routeManager
				.GetNavBarRoutes()
				.Filter((route) => {
					// Only include routes that are in the drawer navigation enum
					const isDrawerRoute = Object.values(DrawerNavigationRoute).includes(
						route.Name as DrawerNavigationRoute
					);
					if (!isDrawerRoute) {
						return false;
					}
					if (route.Name === "Dashboard") {
						return true;
					}
					if (route.GetBusinessObj().IsSome()) {
						return true;
					}
					if (route.Name === "Upload") {
						return (
							permissions.createContainmentPlantIds.length > 0 ||
							permissions.asrsLockPlantIds.length > 0
						);
					}
					return false;
				})
					.Map((menuItem, index) => {
						const path = menuItem.Path;
						const displayName =
							menuItem.Name === "UserManagement"
								? "User Management"
								: menuItem.Name === "Upload"
									? "New Containment"
									: menuItem.Name;
						return (
							<StyledNavLink
								key={path}
								to={path}
								onClick={() => onClick(path, index)}
								className={({ isActive }) =>
									isActive ? "active" : ""
								}
							>
								<ListItemButton>
									<ListItemIcon>
										{getIconForRoute(menuItem.Name)}
									</ListItemIcon>
									<ListItemText
										primary={displayName}
										primaryTypographyProps={{
											noWrap: true,
										}}
									/>
								</ListItemButton>
							</StyledNavLink>
						);
					})}
			</StyledList>
		</StyledDrawer>
	);
};
